import { MutableRefObject } from 'react'

import type {
  QueryClient,
  QueryKey,
  UseQueryResult,
} from '@tanstack/react-query'

import type { PaginatedResponse } from '@/types/Api'

interface CombinePerItemSearchResultsConfig<T> {
  searchTerm: string
  queryClient: QueryClient
  itemIdFn: (item: T) => string
  searchQueryKeyFn: (query: string) => QueryKey
  itemCount: number
}

/**
 * Factory that returns a `combine` callback for `useQueries` where the query
 * array is laid out as `[...perItemQueries, searchQuery]`.
 *
 * Each selected item has its own stable cache entry so toggling one selection
 * never invalidates the others.
 */
export const combinePerItemSearchResults = <T>({
  searchTerm,
  queryClient,
  itemIdFn,
  searchQueryKeyFn,
  itemCount,
}: CombinePerItemSearchResultsConfig<T>) => {
  return (queries: UseQueryResult<T | PaginatedResponse<T> | undefined>[]) => {
    const perItemQueries = queries.slice(0, itemCount)
    const searchQuery = queries[itemCount]

    // Search data with prefix-walk fallback for smooth typeahead
    let searchData: T[] = []
    const searchResponse = searchQuery.data as PaginatedResponse<T> | undefined
    if (searchResponse?.data && !searchQuery.isFetching) {
      searchData = searchResponse.data
    } else if (searchQuery.isFetching) {
      for (let i = searchTerm.length; i > 0; i--) {
        const slicedTerm = searchTerm.slice(0, i)
        const queryKey = searchQueryKeyFn(slicedTerm)
        const previousData =
          queryClient.getQueryData<PaginatedResponse<T>>(queryKey)
        if (previousData?.data) {
          searchData = previousData.data
          break
        }
      }
    }

    // Collect resolved per-item data (only shown when not searching)
    const itemsData: T[] = []
    if (!searchTerm) {
      for (const q of perItemQueries) {
        if (q.data) itemsData.push(q.data as T)
      }
    }

    const searchResultsAreEmpty =
      searchTerm.length > 0 &&
      searchResponse != null &&
      searchResponse.data.length === 0

    // Deduplicate: search results first, then selected items
    const seen = new Map<string, T>()
    for (const item of searchData) {
      seen.set(itemIdFn(item), item)
    }
    if (!searchResultsAreEmpty && itemsData.length > 0) {
      for (const item of itemsData) {
        if (!seen.has(itemIdFn(item))) {
          seen.set(itemIdFn(item), item)
        }
      }
    }

    const data = Array.from(seen.values())

    const isLoading =
      perItemQueries.some((q) => q.isLoading) || searchQuery.isLoading
    const isPending =
      perItemQueries.some((q) => q.isPending) || searchQuery.isPending
    const isFetching =
      perItemQueries.some((q) => q.isFetching) || searchQuery.isFetching

    return { data, isLoading, isPending, isFetching }
  }
}

interface CombineQueryResultsArgs<T, TIdentifier> {
  searchTerm: string
  cacheRef: MutableRefObject<Map<string, T>>
  queryClient: QueryClient
  itemsQuery: UseQueryResult<PaginatedResponse<T> | undefined>
  itemIdFn: (item: T) => string
  itemsQueryKeyFn?: (itemIds: TIdentifier[]) => QueryKey
  searchQuery: UseQueryResult<PaginatedResponse<T> | undefined>
  searchQueryKeyFn: (query: string) => QueryKey
  initialItemIds?: TIdentifier[]
}

export const combineQueryResults = <T, TIdentifier>({
  searchTerm,
  cacheRef,
  queryClient,
  itemsQuery,
  searchQuery,
  itemIdFn,
  itemsQueryKeyFn,
  searchQueryKeyFn,
  initialItemIds,
}: CombineQueryResultsArgs<T, TIdentifier>) => {
  // get data from current search query if it exists and is successful
  let searchData: T[] = []
  if (searchQuery.data?.data && !searchQuery.isFetching) {
    searchData = searchQuery.data.data
  } else if (searchQuery.isFetching) {
    // if fetching, get the previous successful search results
    let previousData: PaginatedResponse<T> | undefined
    for (let i = searchTerm.length; i > 0; i--) {
      const slicedTerm = searchTerm.slice(0, i)
      const queryKey = searchQueryKeyFn(slicedTerm)
      previousData = queryClient.getQueryData<PaginatedResponse<T>>(queryKey)
      if (previousData?.data) {
        searchData = previousData.data
        break
      }
    }
  }

  let itemsData: T[] = []
  if (!searchTerm && initialItemIds) {
    // Cache-first: if all items are already cached, read directly without
    // waiting for a query. This avoids flicker when toggling selections.
    const cached = initialItemIds.map((id) => cacheRef.current.get(String(id)))

    if (cached.every(Boolean)) {
      itemsData = cached as T[]
    } else if (itemsQuery.data?.data) {
      itemsData = itemsQuery.data.data
    } else if (itemsQuery.isFetching && itemsQueryKeyFn) {
      // Legacy fallback: try to find previous cached query data by slicing
      let previousData: PaginatedResponse<T> | undefined
      for (let i = initialItemIds.length; i > 0; i--) {
        const slicedItems = initialItemIds.slice(0, i)
        const queryKey = itemsQueryKeyFn(slicedItems)
        previousData = queryClient.getQueryData<PaginatedResponse<T>>(queryKey)
        if (previousData?.data) {
          itemsData = previousData.data
          break
        }
      }
    }
  }

  const searchResultsAreEmpty =
    searchTerm.length > 0 &&
    searchQuery &&
    searchQuery.data &&
    searchQuery.data.data.length === 0

  let data: T[] = [...searchData]

  // use items from items query as a placeholder
  if (!searchResultsAreEmpty && itemsData.length > 0) {
    data.push(...itemsData)
  }

  // deduplicate items
  data.forEach((item) => cacheRef.current.set(itemIdFn(item), item))

  // convert back to array
  const uniqueIds = new Set(data.map((item) => itemIdFn(item)))
  const uniqueData = Array.from(uniqueIds.values())
    .map((id) => cacheRef.current.get(String(id)))
    .filter((item) => item !== undefined)

  const isLoading = itemsQuery.isLoading || searchQuery.isLoading
  const isPending = itemsQuery.isPending || searchQuery.isPending
  const isFetching = itemsQuery.isFetching || searchQuery.isFetching

  return {
    data: uniqueData,
    isLoading,
    isPending,
    isFetching,
  }
}
